import React, { useEffect, useState } from 'react';
import { Card, CardContent, Typography, Button, Box, Modal } from '@mui/material';
import SubscribeForm from './SubscribeForm2';
import UnsubscribeForm from './UnsubscribeForm';
import BanktransferForm from './BanktransferForm2';
import CamerasForm from './CamerasForm';
import { api, Balance } from '../api';
import { useTranslation } from 'react-i18next';
import { useCustomer } from '../../../context/CustomerContext';
import { formatDateTime } from '../../../utils/util';
import { useFetch } from './useFetch4';

export function calculateCurrentBalance(balance: Balance, currentDate: Date): number {
  if (typeof balance.value === 'string') {
    balance.value = parseFloat(balance.value);
  }
  if (typeof balance.value !== 'number' || isNaN(balance.value)) 
    return 0;

  if (!balance.startDate || !balance.endDate) {
    return balance.value;
  }
  const start = new Date(balance.startDate).getTime();
  const end = new Date(balance.endDate).getTime();
  const now = currentDate.getTime();

  if (now < start) {
    return balance.value;
  }
  if (now > end) {
    return 0;
  }

  const totalDuration = end - start;
  const elapsedDuration = now - start;

  const usedValue = balance.value * (elapsedDuration / totalDuration);
  const remainingBalance = balance.value - usedValue;
  
  return parseFloat(remainingBalance.toFixed(2));
}

type ModalType = 'subscribe' | 'unsubscribe' | 'transfer' | 'cameras';

const BalanceForm: React.FC = () => {
  const { t } = useTranslation();

  const [balance, setBalance] = useState<Balance | null>(null);
  const [currentBalance, setCurrentBalance] = useState<number>(0);
  const [open, setOpen] = useState(false);
  const [modalType, setModalType] = useState('subscribe');
  const [showActions, setShowActions] = useState(false);
  const { customerData, isLoading } = useCustomer();
  const { data: isSubscribed, refresh: refreshIsSubscribed, } = useFetch<boolean>(api.getSubscription, false, customerData?.customer);

  const today = new Date(); 

  useEffect(() => {
    const fetchBalance = async () => {
      try {
        const res = await api.getBalance();
        if (!res || res.value === undefined) {
          console.error(t('BalanceForm.NoBalanceError'));
          return;
        }
        setBalance(res);
      } catch (error) {
        console.error(t('BalanceForm.LoadingError'), error);
        setBalance(null);
      }
    };
    fetchBalance();
  }, [t]);

  useEffect(() => {
    if (!balance) return;
    setCurrentBalance(calculateCurrentBalance(balance, new Date()));
  }, [balance]);

  const handleOpen = (type: ModalType) => {
    setModalType(type);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    refreshIsSubscribed(); // Refresh subscription status after closing any modal
  };

  if (!balance) return (<div>{t('BalanceForm.Loading')}</div>);

  return (
    <>
      <Card sx={{ minWidth: 275 }}>
        <CardContent>
          <Typography variant="h5" component="div" gutterBottom>
            Оплата / керування підпискою
          </Typography>
          <Typography sx={{ mb: 1.5 }} color="text.secondary">
            {t('BalanceForm.PaidUntil')} {formatDateTime(balance.endDate)}
          </Typography>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3, width: '50%' }}>
            <Button variant="outlined" onClick={() => handleOpen('subscribe')} disabled={isSubscribed}>
              Підписка на послуги
            </Button>
            <Button variant="outlined" onClick={() => handleOpen('transfer')}>
              Банківський переказ
            </Button>
            <Button variant="outlined" onClick={() => handleOpen('cameras')}>
              Зміна кількості камер
            </Button>
          </Box>

              <Typography
                 variant="body2"
                 sx={{ cursor: 'pointer', textDecoration: 'underline', mb: 1, mt: 4, }}
                 onClick={() => setShowActions(!showActions)}
              >
                 {showActions ? 'Сховати' : 'Додатково'}
                 {/* {showActions ? t('Setup.HideAdditional') : t('Setup.ShowAdditional')} */}
              </Typography>

               {/* Скрытый контент */}
               {showActions && (
                 <>
                   <Typography variant="h6" gutterBottom>
                     {/* {t('Setup.StopService')} */}
                     {/* Припинити підписку */}
                   </Typography>
                  <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3, width: '50%' }}>
                    <Button variant='outlined' color='info' onClick={() => handleOpen('unsubscribe')}>
                      Припинити підписку
                    </Button>
                  </Box>
                 </>
               )}

        </CardContent>
      </Card>
      <Modal open={open} onClose={handleClose}>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          minWidth: 400,
        }}>
          {modalType == 'subscribe'   && (<SubscribeForm onClose={handleClose} /> )}
          {modalType == 'unsubscribe' && (<UnsubscribeForm onClose={handleClose} /> )}
          {modalType == 'transfer'    && (<BanktransferForm onClose={handleClose} /> )}
          {modalType == 'cameras'     && (<CamerasForm onClose={handleClose} /> )} 
        </Box>
      </Modal>
    </>
  );
};

export default BalanceForm;